import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { authApi } from "@/api/client";

interface AuthContextValue {
  isAdmin: boolean;
  loading: boolean;
  login: (password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authApi
      .session()
      .then((res) => setIsAdmin(res.authenticated))
      .catch(() => setIsAdmin(false))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (password: string) => {
    const res = await authApi.login(password);
    setIsAdmin(res.authenticated);
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setIsAdmin(false);
  }, []);

  const value = useMemo(() => ({ isAdmin, loading, login, logout }), [isAdmin, loading, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
